/**
 * MetricDrawer — slides in from the right when a metric is clicked.
 * Contents:
 *  - Value / status / delta / large sparkline + dashed target line
 *  - Definition + source
 *  - Breakdown dimension switcher (Size · Pipeline · Owner · Project · Type · Severity)
 *    rendering horizontal proportion bars (count + dev-days)
 *  - Tasks table (task · size · pipeline · owner)
 */
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { type ReactElement, useState } from 'react';

import { useScorecardMetric } from '@/hooks/useScorecardMetric';
import type { Metric, ScorecardParams } from '@/types/scorecard';
import { Sparkline } from './Sparkline';

interface MetricDrawerProps {
  metric: Metric | null;
  params: ScorecardParams;
  onClose: () => void;
}

type BreakdownDim = 'size' | 'pipeline' | 'owner' | 'project' | 'type' | 'severity';

const BREAKDOWN_DIMS: { key: BreakdownDim; label: string }[] = [
  { key: 'size', label: 'Size' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'owner', label: 'Owner' },
  { key: 'project', label: 'Project' },
  { key: 'type', label: 'Type' },
  { key: 'severity', label: 'Severity' },
];

const STATUS_COLOR: Record<string, string> = {
  green: '#6BFFAB',
  amber: '#FFB000',
  red:   '#ef4444',
  na:    '#6c6c78',
};

const DELTA_ARROW: Record<string, string> = {
  good: '▲',
  bad: '▼',
  flat: '',
};

function BreakdownDimSwitcher({
  active,
  onChange,
}: {
  active: BreakdownDim;
  onChange: (d: BreakdownDim) => void;
}): ReactElement {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '5px',
        overflow: 'hidden',
        background: 'background.deeper',
        flexWrap: 'wrap',
      }}
      role="group"
      aria-label="Breakdown dimension"
    >
      {BREAKDOWN_DIMS.map(({ key, label }) => (
        <Box
          key={key}
          component="button"
          onClick={() => onChange(key)}
          aria-pressed={active === key}
          aria-label={`Breakdown by ${label}`}
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.5625rem',
            fontWeight: 600,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            px: 1.125,
            minHeight: 44,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 120ms',
            ...(active === key
              ? { background: 'rgba(217,119,87,0.10)', color: '#D97757' }
              : {
                  background: 'transparent',
                  color: 'text.disabled',
                  '&:hover': { color: 'text.primary' },
                }),
          }}
        >
          {label}
        </Box>
      ))}
    </Box>
  );
}

export function MetricDrawer({ metric, params, onClose }: MetricDrawerProps): ReactElement {
  const [activeDim, setActiveDim] = useState<BreakdownDim>('size');
  const { data, isLoading, error } = useScorecardMetric(metric?.key ?? null, params);

  const accentColor = STATUS_COLOR[metric?.status ?? 'na'] ?? '#6c6c78';

  // Compute max count for proportion bars
  const buckets = data?.breakdowns?.[activeDim] ?? [];
  const maxCount = Math.max(...buckets.map((b) => b.count), 1);

  return (
    <Drawer
      anchor="right"
      open={metric !== null}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '92vw', sm: 440 },
          background: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          p: 3,
          overflowY: 'auto',
        },
      }}
      aria-label={metric ? `Detail for ${metric.label}` : 'Metric detail'}
    >
      {metric && (
        <>
          {/* Drawer header */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 0.5 }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.625rem',
                  letterSpacing: '0.7px',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                }}
              >
                {metric.group}
              </Typography>
              <Typography
                variant="h5"
                sx={{ mt: 0.5, mb: 0.25, color: 'text.primary', fontWeight: 700 }}
              >
                {metric.label}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="Close metric detail drawer"
              size="small"
              sx={{
                mt: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                color: 'text.secondary',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* Value + status + delta */}
          <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mt: 2, mb: 0.5 }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: metric.status === 'na' ? '1.05rem' : '2.6rem',
                fontWeight: 700,
                lineHeight: 1,
                color: metric.status === 'na' ? 'text.disabled' : accentColor,
              }}
              aria-label={`Value: ${metric.display}`}
            >
              {metric.display}
            </Typography>
            {metric.unit && metric.status !== 'na' && (
              <Typography sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                {metric.unit}
              </Typography>
            )}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.6875rem',
                fontWeight: 600,
                color:
                  metric.delta.kind === 'good'
                    ? '#6BFFAB'
                    : metric.delta.kind === 'bad'
                    ? '#ef4444'
                    : 'text.disabled',
                ml: 'auto',
              }}
              aria-label={`Change: ${metric.delta.display}`}
            >
              {DELTA_ARROW[metric.delta.kind] && (
                <Box component="span" aria-hidden="true">
                  {DELTA_ARROW[metric.delta.kind]}
                </Box>
              )}
              {metric.delta.display}
            </Box>
          </Stack>

          {/* Target */}
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.6875rem',
              color: 'text.disabled',
              mb: 2.25,
            }}
          >
            Target:{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {metric.target.label}
            </Box>
          </Typography>

          {/* Large sparkline with target line */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              background: 'background.deeper',
              px: 1.25,
              py: 1,
              mb: 0.5,
            }}
          >
            <Sparkline
              data={metric.spark}
              status={metric.status}
              height={64}
              targetLine={metric.target?.prorated ?? metric.target?.value ?? null}
              ariaLabel={`${metric.label} trend over time with target line`}
            />
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2.25 }}>
            <Box
              sx={{
                display: 'inline-block',
                width: 13,
                height: 0,
                borderTop: '1px dashed',
                borderColor: 'text.disabled',
              }}
              aria-hidden="true"
            />
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.5625rem',
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                color: 'text.faintest',
              }}
            >
              dashed = target
            </Typography>
          </Stack>

          {/* Definition + source table */}
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
            <tbody>
              {[
                { label: 'Definition', value: metric.definition },
                { label: 'Source', value: metric.source },
              ].map(({ label, value }) => (
                <Box
                  key={label}
                  component="tr"
                >
                  <Box
                    component="td"
                    sx={{
                      p: '8px 0',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.625rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      width: 110,
                      verticalAlign: 'top',
                    }}
                  >
                    {label}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: '8px 0',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.8125rem',
                      color: 'text.primary',
                      verticalAlign: 'top',
                    }}
                  >
                    {value}
                  </Box>
                </Box>
              ))}
              {metric.na_reason && (
                <Box component="tr">
                  <Box
                    component="td"
                    sx={{
                      p: '8px 0',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.625rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      width: 110,
                      verticalAlign: 'top',
                    }}
                  >
                    N/A reason
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: '8px 0',
                      fontSize: '0.8125rem',
                      color: 'text.secondary',
                      verticalAlign: 'top',
                    }}
                  >
                    {metric.na_reason}
                  </Box>
                </Box>
              )}
            </tbody>
          </Box>

          {/* Reliability section — measured/population/coverage + gate reason */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: metric.reliability.gated ? 'rgba(138,138,148,0.35)' : 'divider',
              borderRadius: '6px',
              background: 'background.deeper',
              p: 1.5,
              mb: 2.5,
            }}
            aria-label="Reliability"
          >
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.625rem',
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: 'text.disabled',
                mb: 1,
              }}
            >
              Reliability
            </Typography>
            <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mb: metric.reliability.gate_reason ? 1 : 0 }}>
              <Stack spacing={0.125}>
                <Typography sx={{ fontSize: '0.625rem', color: 'text.faintest', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Measured
                </Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem', fontWeight: 700, color: 'text.primary' }}>
                  {metric.reliability.measured}
                </Typography>
              </Stack>
              <Stack spacing={0.125}>
                <Typography sx={{ fontSize: '0.625rem', color: 'text.faintest', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Population
                </Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem', fontWeight: 700, color: 'text.primary' }}>
                  {metric.reliability.population}
                </Typography>
              </Stack>
              <Stack spacing={0.125}>
                <Typography sx={{ fontSize: '0.625rem', color: 'text.faintest', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Coverage
                </Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem', fontWeight: 700, color: 'text.primary' }}>
                  {metric.reliability.coverage_pct === null ? '—' : `${metric.reliability.coverage_pct}%`}
                </Typography>
              </Stack>
              <Stack spacing={0.125}>
                <Typography sx={{ fontSize: '0.625rem', color: 'text.faintest', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Min sample
                </Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem', fontWeight: 700, color: 'text.primary' }}>
                  {metric.reliability.min_n}
                </Typography>
              </Stack>
            </Stack>
            {metric.reliability.gate_reason && (
              <Typography
                role="status"
                sx={{
                  fontSize: '0.75rem',
                  color: '#8a8a94',
                  fontStyle: 'italic',
                }}
              >
                {metric.reliability.gate_reason}
              </Typography>
            )}
            {metric.ramping && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'primary.main',
                  mt: metric.reliability.gate_reason ? 0.75 : 0,
                }}
              >
                Ramping — window is still building history since the 14-Jun cutoff.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Breakdown section */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }} role="status">
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              Failed to load breakdown: {error.message}
            </Alert>
          )}

          {data && (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.25}
                sx={{ mb: 1.5 }}
              >
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.625rem',
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                  }}
                >
                  Breakdown
                </Typography>
                <BreakdownDimSwitcher active={activeDim} onChange={setActiveDim} />
              </Stack>

              {/* Proportion bars */}
              {buckets.length === 0 ? (
                <Typography
                  sx={{ fontSize: '0.8125rem', color: 'text.disabled', py: 1 }}
                >
                  No data for this dimension.
                </Typography>
              ) : (
                <Box sx={{ mb: 2.5 }}>
                  {buckets.map((b) => (
                    <Box
                      key={b.key}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '96px 1fr auto',
                        alignItems: 'center',
                        gap: 1.25,
                        mb: 0.875,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.6875rem',
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={b.key}
                      >
                        {b.key}
                      </Typography>
                      <Box
                        sx={{
                          height: 9,
                          borderRadius: '3px',
                          background: 'background.deeper',
                          overflow: 'hidden',
                        }}
                        role="img"
                        aria-label={`${b.key}: ${b.count} tasks`}
                      >
                        <Box
                          sx={{
                            display: 'block',
                            height: '100%',
                            width: `${(b.count / maxCount) * 100}%`,
                            bgcolor: 'primary.main',
                            opacity: 0.85,
                            borderRadius: '3px',
                            transition: 'width 200ms ease',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.6875rem',
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {b.count}
                        {b.dev_days > 0 && (
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            {' '}
                            {b.dev_days.toFixed(1)}d
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Tasks table */}
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.625rem',
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                  mb: 1,
                }}
              >
                Contributing tasks
              </Typography>

              {data.tasks.length === 0 ? (
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>
                  No tasks in this period.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box
                    component="table"
                    sx={{ width: '100%', borderCollapse: 'collapse' }}
                    aria-label="Contributing tasks"
                  >
                    <Box component="thead">
                      <Box component="tr">
                        {['Task', 'Size', 'Stage', 'Owner', 'Notion'].map((h) => (
                          <Box
                            key={h}
                            component="th"
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '0.5rem',
                              fontWeight: 700,
                              letterSpacing: '0.5px',
                              textTransform: 'uppercase',
                              color: 'text.faintest',
                              textAlign: 'left',
                              p: '6px 8px',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {h}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {data.tasks.map((task) => (
                        <Box key={task.notion_id} component="tr">
                          <Box
                            component="td"
                            sx={{
                              p: '7px 8px',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              fontSize: '0.75rem',
                              color: 'text.primary',
                              maxWidth: 200,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: 'text.primary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 200,
                              }}
                              title={task.title}
                            >
                              {task.title}
                            </Typography>
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              p: '7px 8px',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {task.size && (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  fontFamily: '"JetBrains Mono", monospace',
                                  fontSize: '0.5625rem',
                                  fontWeight: 700,
                                  px: 0.75,
                                  py: '1px',
                                  borderRadius: '3px',
                                  background: 'background.elev2',
                                  color: 'text.secondary',
                                }}
                              >
                                {task.size}
                              </Box>
                            )}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              p: '7px 8px',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                            }}
                          >
                            {task.stage}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              p: '7px 8px',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: '"JetBrains Mono", monospace',
                                color: 'text.disabled',
                                fontSize: '0.6875rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 120,
                              }}
                              title={task.owner ?? ''}
                            >
                              {task.owner ?? '—'}
                            </Typography>
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              p: '7px 8px',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {task.notion_url ? (
                              <Box
                                component="a"
                                href={task.notion_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open "${task.title}" in Notion`}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  minHeight: 44,
                                  fontFamily: '"JetBrains Mono", monospace',
                                  fontSize: '0.6875rem',
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' },
                                  '&:focus-visible': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.main',
                                    outlineOffset: 2,
                                  },
                                }}
                              >
                                Notion
                                <OpenInNewIcon sx={{ fontSize: '0.8125rem' }} aria-hidden="true" />
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled' }}>—</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Loading skeleton for the breakdown / tasks section */}
          {isLoading && !data && (
            <Box sx={{ mt: 2 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Stack key={i} direction="row" spacing={1.25} sx={{ mb: 1 }}>
                  <Skeleton variant="text" width={80} height={14} />
                  <Skeleton variant="rectangular" sx={{ flex: 1, height: 9, borderRadius: '3px', mt: 0.375 }} />
                  <Skeleton variant="text" width={40} height={14} />
                </Stack>
              ))}
            </Box>
          )}
        </>
      )}
    </Drawer>
  );
}
